/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import React, { useRef, useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { taskInspectionAndProgressFormUrl } from "@Services/dataManager";
import { taskInspectionAndProgressFormsController, formUserSettingsController } from "@Actions/Forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { formCategories, presetSectionsCodeObject, getInspectionUrl } from "@Helpers";
import InspectionCreation from "./InspectionCreation";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { formStatuses, formStatusMapper } from "./constants";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { DisabledIllustration } from "@Features/commercial-shared/contracts/contractHelpers";
import Badge from "@mui/material/Badge";
import useFromList from "./useFromList";
import Text from "@CubeComponents/html/Text/Text";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Box } from "@mui/material";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

interface FormsProps {
  formCategory: string;
  source?: string;
  showOnlyApproved?: boolean;
  customQuery?: any;
  showOnlyGrid?: boolean;
  allowRecordClick?: boolean;
  showNavigateColumn?: boolean;
  setIsRowSelected?: any;
  // setCount?: any;
}
const TasksForms = forwardRef(
  (
    {
      formCategory,
      source = "",
      showOnlyApproved,
      customQuery,
      showOnlyGrid = false,
      allowRecordClick = true,
      showNavigateColumn = false,
      setIsRowSelected,
      // setCount,
    }: FormsProps,
    ref,
  ) => {
    const { t } = useTranslation();
    // Typed, not validated: the backend route schema validates this id and 400s if malformed.
    const params = useParams() as { projectId: ObjectIdString; contractId?: string };
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.userDetails);
    const contractDetails = useSelector((state: RootState) => state.contractDetails);
    const [showFormFlowCreationModal, setShowFormFlowCreationModal] = useState({
      status: false,
      category: formCategory,
    });
    const [showArchivedForms, setShowArchivedForms] = useState(false);

    const [toolBarBtnState, setToolBarBtnState] = useState({
      delete: false,
      archive: false,
      unArchive: false,
    });

    const {
      userSettingsExists,
      resetConfig,
      showWarning,
      showWarningModal,
      hideWarningModal,
      resetConfigClick,
      moduleTxt,
    }: {
      userSettingsExists: any;
      resetConfig: any;
      showWarning: any;
      showWarningModal: any;
      hideWarningModal: any;
      resetConfigClick: any;
      moduleTxt: any;
    } = useFromList({
      projectId: params.projectId,
      formCategory: formCategory,
      showOnlyGrid,
    });

    const tempRef = useRef<any>(null);
    const dataGrid: any = ref || tempRef;
    const dispatch = useDispatch();

    const isModuleDisabled = params.contractId && !contractDetails?.modulesEnabled[formCategory];

    const initialQuery: any = {
      projectId: params.projectId,
      resourceType: formCategory,
      contractId: params.contractId,
      contractVersionId: contractDetails?.versionId,
      params: {
        projectId: params.projectId,
        resourceType: formCategory,
        contractId: params.contractId,
        contractVersionId: contractDetails?.versionId,
      },
      where: [
        {
          isComplex: false,
          field: "isArchived.status",
          operator: "equal",
          ignoreCase: false,
          value: showArchivedForms,
        },
      ],
    };

    if (source == "contract" && showOnlyApproved) {
      initialQuery.where = [
        {
          isComplex: true,
          ignoreCase: false,
          condition: "and",
          predicates: [
            {
              isComplex: false,
              field: "isArchived.status",
              operator: "equal",
              ignoreCase: false,
              value: showArchivedForms,
            },
            {
              isComplex: false,
              field: "isApproved",
              operator: "equal",
              ignoreCase: false,
              value: true,
            },
          ],
        },
      ];
    }

    const [query, setQuery] = useState(customQuery || initialQuery);

    useEffect(() => {
      const updatedQuery = customQuery || initialQuery;
      setQuery(updatedQuery);
    }, [showArchivedForms, formCategory, customQuery]);

    const progressTemplate = (args: any) => {
      let totalMandatoryFields = 0;
      let mandatoryFieldsFilled = 0;

      args.selectedForm.template.map((section: any) => {
        if (section.questionFields) {
          if (section.sectionCode != presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION) {
            section.questionFields.map((field: any) => {
              const answerValue = field.answer?.value;
              if (field.isMandatory) {
                totalMandatoryFields = totalMandatoryFields + 1;
                if (
                  !(
                    ["", undefined, null, [], 0].includes(answerValue) ||
                    (Array.isArray(answerValue) && answerValue?.join("")?.trim() == "")
                  )
                ) {
                  mandatoryFieldsFilled = mandatoryFieldsFilled + 1;
                }
              }
            });
          }
        }
      });
      const formCompletionPercent = Number(((mandatoryFieldsFilled * 100) / totalMandatoryFields).toFixed(2)) || 0;

      return `${formCompletionPercent}%`;
    };

    const handleFormOpenClick = (e: any, args: any) => {
      e.stopPropagation();

      const location = getInspectionUrl({
        resourceType: args.resourceType,
        resourceId: args._id,
        projectId: args.projectId,
        contractId: args.contractId,
      });

      window.open(location, "_blank");
    };

    const navigateTemplate = (args: any) => {
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="cursor-pointer">
          <IconComponent
            onClick={(e: React.MouseEvent) => handleFormOpenClick(e, args)}
            className="cursor-pointer"
            name="openInViewer"
            width="1.25rem"
            height="1.25rem"
          />
        </div>,
      );
      return container;
    };
    const statusTemplate = (args: any) => {
      let status = "";
      let color;
      const stage = args.status;
      if (stage == formStatuses.inProgress) {
        status = "In progress";
        color = "bg-system_colour_12";
      }
      if (stage == formStatuses.submitted) {
        status = "Submitted";
        color = "bg-system_colour_45";
      }
      if (stage == formStatuses.approved) {
        status = "Approved";
        color = "bg-positive_communication";
      }
      if (stage == formStatuses.rejected) {
        status = "Rejected";
        color = "bg-system_colour_8";
      }
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-30 rounded-2xl pe-3 flex items-center`}>
          <span className={`${color} rounded-full h-3 w-3`} />
          <span className="font-semibold">{status}</span>
        </div>,
      );
      return container;
    };

    function formatCreatedDate(data: any, type: any) {
      let date;
      if (type === "lastAction") {
        if (data.lastAction.timeStamp == null) {
          return "-";
        }
        date = data.lastAction.timeStamp;
      } else {
        date = data[type];
      }
      let showDueDateMarker = false;
      let content = untranslated("");
      let color = "";
      if (type == "dueDate") {
        const today = Date.now();
        const threeDaysFromNow = today + 259200000;
        if (date <= threeDaysFromNow) {
          showDueDateMarker = true;
          const days = ((date - today) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date approaching in {{days}} days", { days });
          color = "#ffcf55";
        }
        if (date <= today) {
          showDueDateMarker = true;
          const days = ((today - date) / (1000 * 60 * 60 * 24)).toFixed(2);
          content = t("Due date exceeded by {{days}} days", { days });
          color = "#e75555";
        }
      }
      const dateFormat = convertDate(Number(date), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="flex items-center gap-3">
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(date), "dateForText")}</span>
          </ToolTipComponent>
          {showDueDateMarker && (
            <IconComponent
              showGradient
              color={color}
              name="details"
              width="1.5rem"
              height="1.5rem"
              position="top"
              toolTipContent={content}
            />
          )}
        </div>,
      );
      return container;
    }

    function createdByTemplate(params: any, type: any) {
      let _id;
      if (type === "lastAction") {
        if (params.lastAction.userId == "") {
          return "-";
        }
        _id = params.lastAction.userId;
      } else {
        _id = params.createdBy._id;
      }

      const container = document.createElement("div");
      renderUserTag(container, { id: _id });
      return container;
    }

    const columnDefinitions: any = useRef([
      ...(showNavigateColumn
        ? [
            {
              field: "_id",
              name: "",
              minWidth: 100,
              formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) => navigateTemplate(value),
            },
          ]
        : []),
      {
        field: "counter",
        id: "counter",
        name: t("Form No."),
        filterType: "Number",
        minWidth: 100,
      },
      {
        field: "title",
        id: "title",
        filterType: "Text",
        name: t("Title"),
        minWidth: 150,
      },
      {
        field: "description",
        id: "description",
        filterType: "Text",
        name: t("Description"),
        minWidth: 120,
      },
      {
        field: "space.spaceName",
        id: "spaceName",
        filterType: "Text",
        name: t("Space Name"),
        minWidth: 150,
        _valueGetter: (row: any) => row?.space?.spaceName,
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) => value.space.spaceName,
      },
      {
        field: "status",
        id: "status",
        name: t("Status"),
        width: 150,
        filterType: "Text",
        minWidth: 150,
        _valueGetter: (row: any) => row?.status,
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) => statusTemplate(value),
      },
      {
        field: "selectedForm.name",
        id: "selectedForm.name",
        name: t("Template Attached"),
        minWidth: 150,
        filterType: "Text",
        _valueGetter: (row: any) => row?.selectedForm?.name,
      },
      {
        field: "selectedForm.type.name",
        id: "selectedForm.type.name",
        filterType: "Text",
        name: t("Attached Template Type"),
        minWidth: 180,
      },
      {
        field: "selectedFormFlow.name",
        id: "selectedFormFlow.name",
        name: t("Form Flow"),
        minWidth: 120,
        filterType: "Text",
      },
      {
        field: "createdBy.fullName",
        id: "createdBy.fullName",
        name: t("Raised by"),
        minWidth: 150,
        filterType: "Text",
        _valueGetter: (row: any) => row?.createdBy?.fullName,
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
          createdByTemplate(value, "createdBy"),
      },
      {
        field: "createdDate",
        id: "createdDate",
        name: t("Raised On"),
        minWidth: 120,
        filterType: "Date",
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
          formatCreatedDate(value, "createdDate"),
      },
      {
        field: "dueDate",
        id: "dueDate",
        name: t("Due Date"),
        minWidth: 200,
        filterType: "Date",
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
          value.dueDate ? formatCreatedDate(value, "dueDate") : "-",
      },
      {
        field: "formProgress",
        id: "formProgress",
        name: t("Form Progress"),
        minWidth: 150,
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) => progressTemplate(value),
      },
      {
        field: "currentLevelName",
        id: "currentLevelName",
        name: t("Current Level"),
        filterType: "Text",
        minWidth: 150,
      },
      {
        field: "lastAction.userName",
        id: "lastAction.userName",
        name: t("Last Action By"),
        minWidth: 150,
        filterType: "Text",
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
          createdByTemplate(value, "lastAction"),
      },
      {
        field: "lastAction.actionName",
        id: "lastAction.actionName",
        name: t("Last Action"),
        minWidth: 120,
        filterType: "Text",
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) => value.lastAction.actionName,
      },
      {
        field: "lastAction.timeStamp",
        id: "lastAction.timeStamp",
        name: t("Last Action Time"),
        minWidth: 150,
        formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
          formatCreatedDate(value, "lastAction"),
      },
      {
        field: "contractOrgName",
        id: "contractOrgName",
        name: t("Org Name"),
        minWidth: 150,
      },
      {
        field: "contractName",
        id: "contractName",
        name: t("Contract Name"),
        minWidth: 150,
      },
      {
        field: "contractType",
        id: "contractType",
        name: t("Contract Type"),
        minWidth: 150,
      },
    ]);

    const handleChange = () => {
      setShowArchivedForms((prev: any) => !prev);
    };

    const dropDownTemplate = () => (
      <SelectDropDown
        select={handleChange}
        cssClass="e-caret-hide"
        bgColor="transparent"
        items={
          showArchivedForms
            ? [{ text: t("Show Active Forms"), id: "showArchive" }]
            : [{ text: t("Show Archived Forms"), id: "showActive" }]
        }
      >
        <IconComponent name="threedots" width="1.25rem" height="1.25rem" className="cursor-pointer" />
      </SelectDropDown>
    );

    const archivedChipTemplate = () => (
      <span className="rounded-2xl ps-4 pe-4 pt-1 pb-1 text-sm bg-text_light text-white">
        <Text>{t("Archived Forms")}</Text>
      </span>
    );

    const formConfigTemplate = () => (
      <Box
        onClick={() => toolbarClickHandler("reset_form_config")}
        className="flex cursor-pointer items-center pe-3 gap-2"
      >
        <IconComponent name="rollbackReviewIcon" width="1rem" height="1rem" />
        <Text>{t("Reset Form Configuration")}</Text>
        {userSettingsExists && (
          <ToolTipComponent
            showTipPointer={true}
            position={"top"}
            content={t(
              "User settings exists, Meaning form will directly be created on clicking of create button skipping all form creation steps. To reset click here ",
            )}
          >
            <Badge color="primary" variant="dot" />
          </ToolTipComponent>
        )}
      </Box>
    );

    const toolbarOptions: any = [
      ...(formCategory != formCategories.changeOrder && (source == "project" || params.contractId) && !showArchivedForms
        ? [
            {
              name: t("Create"),
              prefixIcon: "add",
              id: "custom_create",
            },
          ]
        : []),
      ...(!showArchivedForms
        ? [
            {
              name: t("Archive"),
              prefixIcon: "archive",
              id: "custom_archive",
              disabled: !toolBarBtnState.archive,
            },
            {
              name: t("Delete"),
              prefixIcon: "delete",
              id: "custom_delete",
              disabled: !toolBarBtnState.delete,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              name: t("Un-Archive"),
              prefixIcon: "unarchive",
              id: "custom_unArchive",
              disabled: !toolBarBtnState.unArchive,
            },
          ]
        : []),
      ...(showArchivedForms
        ? [
            {
              template: archivedChipTemplate,
              align: "Left" as const,
            },
          ]
        : []),
      ...(formCategory != formCategories.changeOrder
        ? [
            {
              name: t("Reset Form Configuration"),
              template: formConfigTemplate,
              id: "reset_form_config",
              rightAllign: true,
            },
          ]
        : []),
      {
        template: dropDownTemplate,
        rightAllign: true,
      },
    ];

    const handleDeleteForms = async () => {
      const selectedItem = dataGrid.current.slickGrid
        .getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
      const resourceIds = selectedItem.map((data: any) => data._id);
      const res = (await taskInspectionAndProgressFormsController({
        action: "delete",
        resourceIds,
        resourceType: formCategory,
      })) as any;
      if (res.data.status) {
        hideWarningModal();
        dispatch(
          showToastNotification({
            content:
              res.data?.message ?? res.data?.warningMessage ?? `Selected ${moduleTxt} have been deleted successfully`,
            type: res.data?.warningMessage ? TOAST_TYPE_WARNING : TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid?.current?.refresh();
      } else {
        hideWarningModal();
        dispatch(
          showToastNotification({
            content: res.data?.errorMessage,
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    };

    const changeFormState = async (state: any) => {
      let status;
      let message;
      if (state == "custom_archive") {
        status = true;
        message = "Archived";
      } else {
        status = false;
        message = "Un-Archived";
      }
      const allselectedRows = dataGrid.current.slickGrid
        .getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));

      const allIds = allselectedRows.map((data: any) => data._id);
      const res = (await taskInspectionAndProgressFormsController({
        action: "archive",
        resourceIds: allIds,
        data: { isArchived: { status } },
      })) as any;
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected form(s) successfully {{message}}", { message: message }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        dataGrid.current.refresh();
      }
    };

    const toolbarClickHandler = async (args: any) => {
      if (args === "custom_delete") {
        showWarningModal("delete");
      }
      if (args === "custom_create") {
        if (userSettingsExists) {
          const res = (await formUserSettingsController({
            action: "createForm",
            projectId: params.projectId,
            resourceType: formCategory,
          })) as any;
          if (res.data.status) {
            const location = getInspectionUrl({
              resourceId: res.data.id,
              projectId: params.projectId,
              resourceType: formCategory,
              contractId: res.data.contractId,
            });
            navigate(location);
            dispatch(
              showToastNotification({
                content: t("Form created successfully"),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          } else {
            dispatch(
              showToastNotification({
                content: res.data.errorMessage,
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        } else {
          setShowFormFlowCreationModal({
            status: true,
            category: formCategory,
          });
        }
      }
      if (args === "custom_archive") {
        changeFormState("custom_archive");
      }
      if (args === "custom_unArchive") {
        changeFormState("custom_unArchive");
      }
      if (args === "reset_form_config") {
        resetConfigClick();
      }
    };

    const getInspectionFormFLow = () => (
      <InspectionCreation
        projectId={params.projectId}
        showFormFlowCreationModal={showFormFlowCreationModal}
        setShowFormFlowCreationModal={setShowFormFlowCreationModal}
        source={params.contractId ? "contract" : "project"}
        spaceId={params.contractId ? contractDetails.space?._id : null}
      />
    );

    const selectionEvent = (args: any) => {
      if (!dataGrid?.current) return;

      const totalRowsSelected = args.detail.args.rows.length;
      if (setIsRowSelected) {
        setIsRowSelected(totalRowsSelected > 0);
      }
      if (showOnlyGrid) {
        return;
      }
      if (totalRowsSelected == 0) {
        setToolBarBtnState({
          delete: false,
          archive: false,
          unArchive: false,
        });
      }
      if (totalRowsSelected >= 1) {
        const btnState = {
          delete: true,
          archive: true,
          unArchive: true,
        };
        const allselectedRows = dataGrid.current.slickGrid
          .getSelectedRows()
          .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));

        allselectedRows.map((record: any) => {
          if (record.createdBy._id != userDetails.id) {
            btnState.delete = false;
          }
        });
        setToolBarBtnState({
          ...btnState,
        });
      }
    };

    const recordClick = (args: any) => {
      if (showOnlyGrid) {
        return;
      }
      const rowIndex = args.detail.args.row;
      const clickedRow = dataGrid.current.slickGrid.getDataItem(rowIndex);

      if (args.detail.args.cell != 0 && !clickedRow.isArchived?.status && source !== "forms" && allowRecordClick) {
        const location = getInspectionUrl({
          resourceType: clickedRow.resourceType,
          resourceId: clickedRow._id,
          projectId: clickedRow.projectId,
          contractId: clickedRow.contractId,
        });
        navigate(location);
      }
    };

    // const created = () => {
    //   setGridReady(true);
    // };

    // const dataBound = () => {
    //   if (setCount) {
    //     setCount(dataGrid.current?.pageSettings?.properties?.totalRecordsCount);
    //   }
    // };

    const renderOption = (props: any, option: any, currentColumnId: any) => {
      if (currentColumnId == "status") {
        return formStatusMapper[option];
      }
    };

    const getGridComponent = () => (
      <SlickGrid1
        key={formCategory}
        ref={dataGrid}
        remoteDataSource={taskInspectionAndProgressFormUrl}
        query={query}
        toolbar={showOnlyGrid ? [] : toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={selectionEvent}
        infiniteScroll
        renderOption={renderOption as any}
        onClick={recordClick}
        enableSortingOption
        columnDefinitions={columnDefinitions.current}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        enableViews={!showOnlyGrid && Boolean(params.projectId)}
        {...(!showOnlyGrid &&
          Boolean(params.projectId) && {
            viewsOptions: {
              headerName: "Views",
              name: "View",
              resourceType: formCategory,
              selectedLayoutKey: `${formCategory}_SELECTED_LAYOUT`,
              fetchParams: { projectId: params.projectId },
            },
          })}
      />
    );
    const warningModal = () => {
      const source = showWarning.source;
      let btnText;
      let contentTxt;
      let headerText;
      let callBackFun;
      switch (source) {
        case "delete":
          btnText = "Delete";
          contentTxt = `${
            formCategory == formCategories.changeOrder ? "All Associated changes in the contract will also be lost" : ""
          } Are you sure you want to delete all selected ${moduleTxt} ?`;
          headerText = t("Delete {{module}}", { module: moduleTxt });
          callBackFun = handleDeleteForms;
          break;
        case "resetConfig":
          btnText = "Reset";
          contentTxt = "Are you sure you want to reset the saved configuration.?";
          headerText = t("Reset Configuration");
          callBackFun = resetConfig;
          break;
      }
      return (
        <ModalStateDialogComponent
          footerContent={
            <div className="flex gap-5 items-center">
              <ButtonComponent onClick={callBackFun} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                {btnText}
              </ButtonComponent>
              <ButtonComponent onClick={hideWarningModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          width="30%"
          headerTitle={headerText}
          onCancel={hideWarningModal}
          isVisible={showWarning.status}
          modelContent={<div className="">{contentTxt} </div>}
        />
      );
    };

    return (
      <div className="h-full w-full overflow-auto flex">
        {showWarning.status && warningModal()}
        {showFormFlowCreationModal.status && getInspectionFormFLow()}
        {isModuleDisabled ? <DisabledIllustration /> : getGridComponent()}
      </div>
    );
  },
);
TasksForms.displayName = "TasksForms";
export default TasksForms;
